import { useEffect, useMemo, useRef, useState } from 'react';
import type { BulkOperationsPort } from '../api/port';
import { BulkOperationResultDialog } from './BulkOperationResultDialog';
import { getFixtureBulkOperation, saveFixtureBulkOperation } from '../fixtures/bulkOperationStore';
import type { BulkOperationDetail } from '../model/bulkOperations';
import { isBulkJobActive } from '../model/presentation';
import type { BulkOperationsRoutePort } from '../routing/port';

type Props = {
  jobId: string;
  port: BulkOperationsPort;
  route: BulkOperationsRoutePort;
  fixtureMode: boolean;
  canCreate: boolean;
  canRun: boolean;
  canReadStaffing: boolean;
  allowedOperationTypes?: readonly string[];
};

export function BulkOperationDetailDialog({ jobId, port, route, fixtureMode, canCreate, canRun, canReadStaffing, allowedOperationTypes }: Props) {
  const context = useMemo(() => ({ jobId, port, fixtureMode }), [jobId, port, fixtureMode]);
  const currentContext = useRef(context);
  currentContext.current = context;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [resource, setResource] = useState<{ context: typeof context; data?: BulkOperationDetail; loading: boolean; error?: string }>({ context, loading: true });
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [submitError, setSubmitError] = useState('');
  const job = resource.context === context ? resource.data : undefined;
  const error = resource.context === context ? resource.error : undefined;
  const loading = resource.context !== context || resource.loading;

  useEffect(() => {
    let active = true;
    let timer: number | undefined;
    const load = async () => {
      setResource((value) => ({ context, data: value.context === context ? value.data : undefined, loading: true }));
      try {
        const data = fixtureMode ? getFixtureBulkOperation(jobId) : await port.getBulkOperation(jobId);
        if (!active) return;
        if (!data || data.id !== jobId) throw new Error('처리 내역을 찾을 수 없습니다.');
        setResource({ context, data, loading: false });
        if (!fixtureMode && isBulkJobActive(data.status)) timer = window.setTimeout(() => void load(), 2_000);
      } catch (cause) {
        if (active) setResource((value) => ({ context, data: value.context === context ? value.data : undefined, loading: false,
          error: cause instanceof Error ? cause.message : '처리 현황을 불러오지 못했습니다.' }));
      }
    };
    void load();
    return () => { active = false; window.clearTimeout(timer); };
  }, [context, fixtureMode, jobId, port, version]);

  const operationAvailable = Boolean(job && (allowedOperationTypes === undefined || allowedOperationTypes.includes(job.operationType)));
  const canSubmit = Boolean(job && canRun && operationAvailable && job.status === 'VALIDATED' && job.totalRows > 0
    && job.validRows === job.totalRows && job.failedRows === 0);
  const reload = () => setVersion((value) => value + 1);
  const submit = async () => {
    if (!job || !canSubmit || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setSubmitError('');
    try {
      const submitted = fixtureMode ? { ...job, status: 'QUEUED', submittedAt: new Date().toISOString() }
        : await port.submitBulkOperation(job.id, `${job.id}:submit`);
      if (!mounted.current || currentContext.current !== context) return;
      const data = { ...job, ...submitted };
      if (fixtureMode) saveFixtureBulkOperation(data);
      setResource({ context, data, loading: false });
      reload();
    } catch (cause) {
      if (mounted.current && currentContext.current === context) setSubmitError(cause instanceof Error ? cause.message : '요청을 제출하지 못했습니다. 다시 시도해 주세요.');
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return <BulkOperationResultDialog job={job} loading={loading} error={error} submitError={submitError || undefined} busy={busy}
    onClose={route.closeJob} onRefresh={reload} onSubmit={canSubmit ? () => void submit() : undefined}
    onCorrect={canCreate && canRun && operationAvailable && job ? () => route.openOperation(job.operationType) : undefined}
    onOpenStaffingChange={canReadStaffing ? route.openStaffingChange : undefined} />;
}
